import type { BoxProps, ButtonProps } from '@chakra-ui/react'
import { Box } from '@chakra-ui/react'
import { ButtonLinkProps } from '@src/components/sco/ButtonLink.tsx'
import type { PaywallGateDensity } from '@src/components/sco/Conversion/PaywallGate'
import { ConversionGate } from '@src/components/sco/Conversion/controller/ConversionGate'
import { ADVISOR_TEAM_PRODUCT_ID, type PaywallIntentId } from '@src/components/sco/Conversion/intent'
import {
  conversionForPermission,
  type ContentPermissionKey,
} from '@src/components/sco/Conversion/permissions'
import { useCurrentUser } from '@src/hooks/useCurrentUser'
import React from 'react'

export type PaywalledActionProps = {
  children?: React.ReactNode
  contentPermission?: string
  isUnlocked?: boolean
  /** Headline for boxed / page / overlay gates. Defaults to the intent's job copy. */
  title?: string
  upgradeBoxProps?: BoxProps
  upgradeCtaLabel?: string
  /**
   * Plan to sell. Ignored when it cannot unlock `contentPermission`: a team-only permission
   * always sells Advisor Team, whatever a caller passes.
   */
  upgradeProductId?: string
  upgradeProductTermId?: string
  /** Styling for the upgrade control (size, variant, width). A passed `href` is not used: a normal click opens checkout, and the anchor points at `/accelerator/payment`. */
  upgradeCtaButtonProps?: ButtonLinkProps
  /** @deprecated Legacy prompt key; every gate now opens the Conversion checkout for its own plan. */
  upgradePrompt?: string
  variant?: 'button-only' | 'boxed' | 'page' | 'overlay' | 'blank'
  /** The founder moment this gate interrupts. Defaults from the permission (`conversionForPermission`). */
  intent?: PaywallIntentId
  tokens?: Record<string, string>
}

const DENSITY_BY_VARIANT: Record<NonNullable<PaywalledActionProps['variant']>, PaywallGateDensity> = {
  'button-only': 'button',
  boxed: 'boxed',
  page: 'page',
  overlay: 'overlay',
  blank: 'blank',
}

/** The plan a gate sells: the caller's choice only when it grants the permission. */
export function paywalledActionProductId(
  contentPermission: string | undefined,
  upgradeProductId: string | undefined
): string | undefined {
  if (!contentPermission) {
    return undefined
  }
  const conversion = conversionForPermission(contentPermission as ContentPermissionKey)
  if (conversion?.productId === ADVISOR_TEAM_PRODUCT_ID) {
    return ADVISOR_TEAM_PRODUCT_ID
  }
  return upgradeProductId || conversion?.productId
}

/**
 * Shared feature gate on the Conversion kit. Unlocked (or entitled by `contentPermission`)
 * renders `children`; otherwise a wall names the plan that grants the permission, at its catalog
 * price, and opens the Conversion checkout for that same plan. Without a permission it is a
 * registration gate. While the viewer loads the gate stays locked.
 */
export const PaywalledAction = ({
  children,
  contentPermission,
  intent,
  isUnlocked,
  title,
  tokens,
  upgradeBoxProps,
  upgradeCtaButtonProps,
  upgradeCtaLabel,
  upgradeProductId,
  upgradeProductTermId,
  variant = 'button-only',
}: PaywalledActionProps) => {
  const { currentUser } = useCurrentUser()
  const density = DENSITY_BY_VARIANT[variant]
  const compact = density === 'button'
  const compactLabel = currentUser?.isGuest ? 'Join to unlock' : 'Upgrade to unlock'
  const { href: _href, ...ctaProps } = (upgradeCtaButtonProps || {}) as ButtonLinkProps & { href?: string }
  const productId = paywalledActionProductId(contentPermission, upgradeProductId)

  const gate = (
    <ConversionGate
      compactCta={compact}
      ctaLabel={upgradeCtaLabel || (compact ? compactLabel : undefined)}
      ctaProps={ctaProps as ButtonProps}
      density={density}
      headline={title}
      intent={intent}
      isUnlocked={isUnlocked}
      permission={contentPermission as ContentPermissionKey | undefined}
      productId={productId}
      termId={upgradeProductTermId}
      tokens={tokens}
    >
      {children}
    </ConversionGate>
  )

  return upgradeBoxProps && !compact ? <Box {...upgradeBoxProps}>{gate}</Box> : gate
}
